import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { toastConstant } from 'constants/toastmessage';
import { useToast } from 'context/ToastContext';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { updateLeadDetails } from 'services/leadServices';
import { updateCaseContactV2 } from 'services/CaseServices';
import { InputNumber } from 'primereact/inputnumber';
import { UpdateContactDialog } from './UpdateContactDialog';
import { Tooltip } from 'primereact/tooltip';
import AddInsurerDialog from 'components/AddInsurerDialog/AddInsurerDialog';

import 'assets/scss/style.scss';
import { useContactList } from 'hooks/useContactList';
import { useDebounce } from 'hooks/useDebounce';
import { listInsurers, createInsurer, updateInsurer } from 'services/insuranceProviderService';
import { listLawftIds } from 'services/Settings';
import { totalCountEvent } from 'services/generalServices';
import { formatDateWithoutTimezone } from 'utils/utils';
import AsyncPaginate from 'components/AsyncPaginate/AsyncPaginate';
import { getGlobalDropdown } from 'services/caseLeadDropdownService';

const Defendant = ({
  control,
  userDetails,
  isLeadSpecific,
  caseDetails,
  handleSubmit,
  fetchLeadDetails,
  getCaseDetails,
  reset,
  setValue,
  watch,
}) => {
  const [defendantNameToggle, setDefendantNameToggle] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState({});
  const [selectedContacts, setSelectedContacts] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [index, setIndex] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [insurerOptions, setInsurerOptions] = useState([]);
  const [showAddInsurerDialog, setShowAddInsurerDialog] = useState(false);
  const [selectedInsurers, setSelectedInsurers] = useState({});
  const [currentDefendantIndex, setCurrentDefendantIndex] = useState(null);
  const [pendingInsurerName, setPendingInsurerName] = useState(null);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [lawftIdList, setLawftIdList] = useState([]);
  const [selectedLawftIdList, setSelectedLawftIdList] = useState({});

  // Search query state for debouncing
  const [searchQuery, setSearchQuery] = useState('');
  const isFirstRender = useRef(true);
  const insuranceCompanyValue = caseDetails?.client_details?.insurance_company;

  // Debounced search query
  const debouncedQuery = useDebounce(searchQuery, 300);

  const { addToast } = useToast();

  const defendants = caseDetails?.defendant;
  const { searchContactsWithFullObjects } = useContactList({
    isCase: !isLeadSpecific,
    isLeads: isLeadSpecific,
  });

  // Get the display value for AutoComplete for a specific defendant
  const getAutoCompleteValue = (contactId) => {
    const selectedContact = selectedContacts[contactId];
    if (selectedContact) {
      return `${selectedContact?.first_name || ''} ${selectedContact?.middle_name || ''} ${selectedContact?.last_name || ''}`
        .replace(/\s+/g, ' ')
        .trim();
    }
    return '';
  };

  // Search function for AutoComplete
  const searchContact = useCallback(async (event) => {
    const query = event.query;

    setSearchQuery(query);
  }, []);

  // Effect to handle debounced search
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedQuery && debouncedQuery.length > 0) {
        const results = await searchContactsWithFullObjects(debouncedQuery);

        // Check if current defendants match the search query and add them to suggestions
        let suggestions = [...results];

        if (defendants && defendants.length > 0) {
          defendants.forEach((defendant) => {
            const defendantName = `${defendant?.first_name || ''} ${defendant?.middle_name || ''} ${defendant?.last_name || ''}`
              .replace(/\s+/g, ' ')
              .trim()
              .toLowerCase();
            const searchQuery = debouncedQuery.toLowerCase();

            // If defendant name contains the search query, add it to suggestions
            if (defendantName.includes(searchQuery)) {
              const defendantSuggestion = {
                label: `${defendant?.first_name || ''} ${defendant?.middle_name || ''} ${defendant?.last_name || ''}`
                  .replace(/\s+/g, ' ')
                  .trim(),
                value: defendant?.contact_id,
                client: defendant, // Full contact object
              };

              // Check if defendant is not already in results
              const isAlreadyInResults = results.some((result) => result.value === defendant?.contact_id);
              if (!isAlreadyInResults) {
                suggestions.unshift(defendantSuggestion); // Add to beginning
              }
            }
          });
        }

        setSuggestions(suggestions);
      } else {
        setSuggestions([]);
      }
    };

    performSearch();
  }, [debouncedQuery, searchContactsWithFullObjects, defendants]);

  const onSelectContact = (contactId, e) => {
    if (e.value && e.value.value) {
      setSelectedContactId((prev) => ({
        ...prev,
        [contactId]: e.value.value,
      }));
      setSelectedContacts((prev) => ({
        ...prev,
        [contactId]: e.value.client,
      }));
      // Update input value to show the selected name
      setInputValues((prev) => ({
        ...prev,
        [contactId]: e.value.label,
      }));
    }
  };

  // Load insurer list from insurance provider API
  const loadInsurers = useCallback(async () => {
    if (!caseDetails?.case_id) return;

    try {
      const response = await listInsurers(caseDetails?.case_id, 15, '', 'forward', insuranceCompanyValue);
      // Handle different possible response structures
      const insurers = response?.data?.items || response?.data || [];

      // Store full insurers list for Lawft ID filtering
      setInsuranceProviders(insurers);

      // Show all insurers without filtering by defendant_insurer flag
      // Use insurer_id as value for uniqueness (insurer names can be duplicated)
      const options = insurers.map((insurer) => {
        // Get claim_number directly from the insurer object
        const claim_number = insurer?.claim_number || '';

        // Get insurance limit - try multiple possible fields
        const insurance_limit = insurer?.insurance_limit || insurer?.coverage_limits?.bodily_injury || 0;

        const insuredPerson = insurer?.insured_person;
        let insuredPersonName = '';
        if (insuredPerson?.first_name || insuredPerson?.last_name) {
          insuredPersonName = `${insuredPerson?.first_name || ''} ${insuredPerson?.last_name || ''}`.trim();
        } else if (insuredPerson?.title) {
          insuredPersonName = insuredPerson.title;
        }
        const label = insuredPersonName
          ? `${insurer?.insurer_name || 'Untitled'} (${insuredPersonName})`
          : insurer?.insurer_name || 'Untitled';

        return {
          label: label,
          value: insurer?.insurer_id, // Use insurer_id as value for uniqueness
          insurer_id: insurer?.insurer_id,
          insurer_name: insurer?.insurer_name || 'Untitled',
          insurer_pk: insurer?.pk,
          insurer_sk: insurer?.sk,
          policy_number: insurer?.policy_number || '',
          claim_number: claim_number,
          insurance_limit: insurance_limit,
        };
      });
      setInsurerOptions(options);
    } catch (error) {
      console.error('Error loading insurers:', error);
      setInsuranceProviders([]);
      setInsurerOptions([]); // Set empty array on error
    }
  }, [caseDetails?.case_id]);

  // Handle Add Insurer Submit
  const onSubmitInsurer = async (data) => {
    try {
      // Get defendant contact_id from the current defendant index
      const defendantContactId = caseDetails?.defendant?.[currentDefendantIndex]?.contact_id;

      // Check if an insurance provider already exists for this defendant
      const existingProvider = caseDetails?.insurance_details?.find(
        (provider) =>
          provider?.defendant_insurer === true &&
          (provider?.insured_person?.contact_id === defendantContactId || provider?.insured_contact_id === defendantContactId)
      );

      // Map form data to API structure
      const insurerData = {
        case_id: caseDetails?.case_id,
        insurer_name: data.insurance_company,
        insured_person: defendantContactId ? { contact_id: defendantContactId } : null,
        contact_name: data.contact_name,
        email: data.email,
        phone_number: data.phone,
        additional_phone: data.additional_phone || '',
        fax: data.fax || '',
        policy_number: data.policy_number,
        claim_number: data.claim_number || '',
        insurance_limit: parseFloat(data.insurance_limit) || 0,
        lawft_id: data.lawft_id || [], // Add lawft_id array
        client_insurer: false,
        defendant_insurer: true, // Mark this as a defendant insurer
      };

      let response;
      if (existingProvider) {
        // Update existing insurance provider instead of creating a new one
        const updateData = {
          ...insurerData,
          insurer_pk: existingProvider.pk,
          insurer_sk: existingProvider.sk,
          insurer_id: existingProvider.insurer_id,
        };
        response = await updateInsurer(updateData);
        addToast(false, toastConstant.toasterType.SUCCESS, 'Success', 'Insurance provider updated successfully');
      } else {
        // Create new insurance provider
        response = await createInsurer(insurerData);
        addToast(false, toastConstant.toasterType.SUCCESS, 'Success', 'Insurance provider added successfully');
      }

      if (response?.data?.insurer_id || response?.data?.message || response?.data) {
        // Set pending insurer name to trigger auto-population after options reload
        if (currentDefendantIndex !== null) {
          setPendingInsurerName(data.insurance_company);
        }

        // Reload insurers list to get the updated insurer
        await loadInsurers();

        // Reload case details to refresh insurance_details
        await getCaseDetails();

        setShowAddInsurerDialog(false);
      }
    } catch (error) {
      console.error('Error adding insurer:', error);
      addToast(false, toastConstant.toasterType.ERROR, 'Error', error?.response?.data?.body || 'Failed to add/update insurance provider');
    }
  };

  const handlePopupConfirmation = (index, contactId) => {
    handleDialogShow(index, contactId);
  };

  const handleDialogShow = (index, contactId) => {
    setShowUpdateDialog(true);
    setIndex(index);
    setContactId(contactId);
  };

  const handleDialogHide = () => {
    setShowUpdateDialog(false);
    setIndex(null);
    setContactId(null);
  };

  const handleRetain = () => {
    const selectedContact = selectedContacts[contactId];
    handleSubmit((data) => handleDefendantSubmit(data, index, contactId, true, selectedContact))();
    setShowUpdateDialog(false);
    setIndex(null);
    setContactId(null);
  };

  const handleReset = () => {
    const selectedContact = selectedContacts[contactId];
    handleSubmit((data) => handleDefendantSubmit(data, index, contactId, false, selectedContact))();
    setShowUpdateDialog(false);
    setIndex(null);
    setContactId(null);
  };

  const handleDefendantSubmit = async (data, index, contactId, isRetain, selectedContact) => {
    let contact = selectedContact || selectedContacts[contactId];

    if (!contact && defendants) {
      contact = defendants.find((defendant) => defendant.contact_id === contactId);
    }

    let updatedClientData = {
      vehicle_info: isRetain ? data?.defendant?.[index]?.vehicle_info : '',
      vehicle_owner: isRetain ? data?.defendant?.[index]?.vehicle_owner : '',
      insurer: isRetain ? data?.defendant?.[index]?.insurer : '',
      policy_number: isRetain ? data?.defendant?.[index]?.policy_number : '',
      claim_number: isRetain ? data?.defendant?.[index]?.claim_number : '',
      name_of_insured: isRetain ? data?.defendant?.[index]?.name_of_insured : '',
      injuries: isRetain ? data?.defendant?.[index]?.injuries : '',
      insurance_limit: isRetain ? data?.defendant?.[index]?.insurance_limit : 0.0,
    };

    const updatedData = { ...contact, ...updatedClientData };

    if (!isLeadSpecific) {
      const defendant_sk = defendants?.find((obj) => obj.contact_id === contactId);
      delete updatedData?.role;
      await updateCaseContactV2(caseDetails?.case_id, updatedData, defendant_sk?.sk);
      await getCaseDetails();

      // Update form fields with the reset values immediately
      if (!isRetain) {
        setValue(`defendant.${index}.policy_number`, '');
        setValue(`defendant.${index}.claim_number`, '');
        setValue(`defendant.${index}.insurance_limit`, 0.0);
        setValue(`defendant.${index}.insurer`, '');
        setValue(`defendant.${index}.vehicle_info`, '');
        setValue(`defendant.${index}.vehicle_owner`, '');
        setValue(`defendant.${index}.name_of_insured`, '');
        setValue(`defendant.${index}.injuries`, '');

        // Clear selected insurer for this defendant
        setSelectedInsurers((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
      }
    } else {
      updateLeadDetails(data.sk, data)
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_UPDATE_SUCCESS);
          fetchLeadDetails();
        })
        .catch((error) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_UPDATE_FAILURE);
        });
    }
    setDefendantNameToggle([]);
  };

  const handleDefendantClaimInfoSubmit = async (data, index, contactId) => {
    const defendant = { ...data?.defendant?.[index] };

    // Add insurer and insurance_company - check multiple sources
    if (selectedInsurers[index]) {
      defendant.insurer = selectedInsurers[index].insurer_id;
      defendant.insurance_company = selectedInsurers[index].insurer_name;
    } else if (defendant.insurer) {
      // If insurer exists in form data but not in selectedInsurers, try to find from insurerOptions
      const matchedInsurer = insurerOptions?.find((opt) => opt.insurer_id === defendant.insurer || opt.value === defendant.insurer);
      if (matchedInsurer) {
        defendant.insurer = matchedInsurer.insurer_id;
        defendant.insurance_company = matchedInsurer.insurer_name;
      }
    } else {
      // If no insurer selected and no insurerId, clear the insurer fields
      defendant.insurer = '';
      defendant.insurance_company = '';
    }

    if (!isLeadSpecific) {
      const defendant_sk = defendants?.find((obj) => obj.contact_id === contactId);
      await updateCaseContactV2(caseDetails?.case_id, defendant, defendant_sk?.sk);
      await getCaseDetails();
    } else {
      updateLeadDetails(data.sk, data)
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_UPDATE_SUCCESS);
          fetchLeadDetails();
        })
        .catch((error) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_UPDATE_FAILURE);
        });
    }
  };

  const toggleVisibility = (rowIndex) => {
    setDefendantNameToggle((prev) => ({
      [rowIndex]: !prev[rowIndex],
    }));
  };

  // Load Lawft IDs and track selected ones
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!caseDetails?.case_id) return;

    const practice_area = {
      practice_area: [`${caseDetails?.case_description?.case_practice_area}`],
      show_unassigned_pa_column: true,
    };

    totalCountEvent('lawft-id')
      .then((response) => {
        listLawftIds(response.data, '', 'forward', 'ACTIVE', practice_area, '', practice_area.practice_area)
          .then((response) => {
            const lawftIds =
              response?.data?.lawft_id.map((lawftId) => ({
                label: lawftId.lawft_id_name,
                value: lawftId.lawft_id || '',
              })) || [];
            setLawftIdList(lawftIds);
          })
          .catch((err) => {
            console.error('Error loading lawft IDs:', err);
            setLawftIdList([]);
          });
      })
      .catch((err) => {
        console.error('Error getting lawft-id count:', err);
      });

    // Track selected Lawft IDs from all contacts
    const selectedLawftId = {};

    // Get Lawft IDs from relations (Person and Business contacts)
    caseDetails?.relations?.forEach((relation) => {
      if (relation?.lawft_id && relation?.contact_id) {
        selectedLawftId[relation.contact_id] = relation.lawft_id;
      }
    });

    // Get Lawft IDs from client_details
    caseDetails?.client_details?.forEach((clientDetail) => {
      if (clientDetail?.lawft_id && clientDetail?.contact_id) {
        selectedLawftId[clientDetail.contact_id] = clientDetail.lawft_id;
      }
    });

    // Get Lawft IDs from defendants
    caseDetails?.defendant?.forEach((defendant) => {
      if (defendant?.lawft_id && defendant?.contact_id) {
        selectedLawftId[defendant.contact_id] = defendant.lawft_id;
      }
    });

    setSelectedLawftIdList(selectedLawftId);
  }, [
    caseDetails?.case_id,
    caseDetails?.case_description?.case_practice_area,
    caseDetails?.relations,
    caseDetails?.client_details,
    caseDetails?.defendant,
  ]);

  // Get all available Lawft IDs excluding those used in Person, Business, and other Insurance Providers
  const getAvailableLawftIdsForInsurer = useCallback(
    (currentInsurerPk = null) => {
      // Get all Lawft IDs used by Person and Business contacts
      const contactLawftIds = Object.values(selectedLawftIdList).flat();

      // Get all Lawft IDs used by other Insurance Providers (excluding current one if editing)
      const otherInsurerLawftIds = insuranceProviders
        .filter((insurer) => insurer.pk !== currentInsurerPk)
        .flatMap((insurer) => insurer.lawft_id || []);

      // Combine all used Lawft IDs
      const allUsedLawftIds = [...contactLawftIds, ...otherInsurerLawftIds];

      // Return available Lawft IDs (excluding used ones)
      return lawftIdList.filter((lawftId) => !allUsedLawftIds.includes(lawftId?.value));
    },
    [selectedLawftIdList, insuranceProviders, lawftIdList]
  );

  // Load insurers on mount and when case details change
  useEffect(() => {
    loadInsurers();
  }, [loadInsurers, caseDetails?.insurance_details]);

  // Set initial defendant values (contacts are auto-loaded by hook)
  useEffect(() => {
    // Set initial defendant values
    if (defendants && defendants.length > 0) {
      const initialSelectedContacts = {};
      const initialSelectedIds = {};
      const initialInputValues = {};

      defendants.forEach((defendant, index) => {
        if (defendant?.contact_id) {
          initialSelectedIds[defendant.contact_id] = defendant.contact_id;
          initialSelectedContacts[defendant.contact_id] = defendant;
          initialInputValues[defendant.contact_id] = `${defendant?.first_name || ''} ${defendant?.middle_name || ''} ${
            defendant?.last_name || ''
          }`
            .replace(/\s+/g, ' ')
            .trim();
        }
      });

      setSelectedContactId(initialSelectedIds);
      setSelectedContacts(initialSelectedContacts);
      setInputValues(initialInputValues);
    }
  }, [defendants]);

  // Map each defendant's insurer ID to insurer name when insurers are loaded
  useEffect(() => {
    if (defendants && defendants.length > 0 && insurerOptions.length > 0) {
      defendants.forEach((defendant, index) => {
        const defendantInsurerId = defendant?.insurer;
        if (defendantInsurerId) {
          // Find the insurer that matches the defendant's insurer ID
          const matchedInsurer = insurerOptions.find((insurer) => insurer?.insurer_id === defendantInsurerId);
          if (matchedInsurer) {
            // Set the insurer name in the form field for this defendant
            setValue(`defendant.${index}.insurer`, matchedInsurer.value);

            // Only update fields if insurer has non-empty values, otherwise keep defendant's existing values
            setValue(`defendant.${index}.policy_number`, matchedInsurer.policy_number || defendant?.policy_number || '');
            setValue(`defendant.${index}.claim_number`, matchedInsurer.claim_number || defendant?.claim_number || '');
            setValue(`defendant.${index}.insurance_limit`, matchedInsurer.insurance_limit || defendant?.insurance_limit || 0);

            // Also set the selected insurer state for this defendant
            setSelectedInsurers((prev) => ({
              ...prev,
              [index]: matchedInsurer,
            }));
          }
        }
      });
    }
  }, [defendants, insurerOptions, setValue]);

  // Set selected insurers when insurerOptions or insurer data changes
  useEffect(() => {
    if (defendants && defendants.length > 0 && insurerOptions.length > 0) {
      const initialSelectedInsurers = {};

      defendants.forEach((defendant, index) => {
        if (defendant?.insurer) {
          // Match by insurer_id (value is now insurer_id)
          const selected = insurerOptions.find((opt) => opt.insurer_id === defendant.insurer);
          if (selected) {
            initialSelectedInsurers[index] = selected;
          }
        }
      });

      setSelectedInsurers(initialSelectedInsurers);
    }
  }, [defendants, insurerOptions]);

  // Auto-populate newly created insurer when insurerOptions updates
  useEffect(() => {
    if (pendingInsurerName && currentDefendantIndex !== null && insurerOptions.length > 0) {
      // Find the newly created insurer by name (pendingInsurerName contains the insurer name)
      const newInsurer = insurerOptions.find((opt) => opt.insurer_name === pendingInsurerName || opt.label === pendingInsurerName);

      if (newInsurer) {
        // Set the insurer_id in the dropdown (value is now insurer_id)
        setValue(`defendant.${currentDefendantIndex}.insurer`, newInsurer.insurer_id);

        // Store in selectedInsurers
        setSelectedInsurers((prev) => ({
          ...prev,
          [currentDefendantIndex]: newInsurer,
        }));

        // Auto-populate fields - only update if insurer has non-empty values, otherwise keep existing
        const currentPolicyNumber = watch(`defendant.${currentDefendantIndex}.policy_number`);
        const currentClaimNumber = watch(`defendant.${currentDefendantIndex}.claim_number`);
        const currentInsuranceLimits = watch(`defendant.${currentDefendantIndex}.insurance_limit`);

        setValue(`defendant.${currentDefendantIndex}.policy_number`, newInsurer.policy_number || currentPolicyNumber || '');
        setValue(`defendant.${currentDefendantIndex}.claim_number`, newInsurer.claim_number || currentClaimNumber || '');
        setValue(`defendant.${currentDefendantIndex}.insurance_limit`, newInsurer.insurance_limit || currentInsuranceLimits || 0);

        // Clear pending state
        setPendingInsurerName(null);
        setCurrentDefendantIndex(null);
      }
    }
  }, [insurerOptions, pendingInsurerName, currentDefendantIndex, setValue, watch]);

  // Create contact options for Vehicle Owner dropdown
  const vehicleOwnerOptions = [
    ...(caseDetails?.client_details?.map((clientDetails) => ({
      label: `${clientDetails.first_name} ${clientDetails.last_name}`,
      value: clientDetails.contact_id,
    })) || []),
    ...(caseDetails?.relations
      ?.filter((relation) => relation?.contact_type === 'business' || relation?.contact_type === 'location')
      .map((relation) => ({
        label: relation.title,
        value: relation.contact_id,
      })) || []),
    ...(caseDetails?.relations
      ?.filter((relation) => relation?.contact_type !== 'business' && relation?.contact_type !== 'location')
      .map((relation) => ({
        label: `${relation.first_name} ${relation.last_name}`,
        value: relation.contact_id,
      })) || []),
  ].sort((a, b) => a.label.localeCompare(b.label));

  const loadInsurerOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit } = additional;

    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const page = currentPage + 1;

    const options = await getGlobalDropdown('', 'insurer', inputValue, caseDetails?.case_id, false, page, pageLimit);

    const updatedOptions = options?.data?.data?.map((insurer) => {
      // Get claim_number directly from the insurer object
      const claim_number = insurer?.claim_number || '';

      // Get insurance limit - try multiple possible fields
      const insurance_limit = insurer?.insurance_limit || insurer?.coverage_limits?.bodily_injury || 0;

      const insuredPerson = insurer?.insured_person;
      let insuredPersonName = '';
      if (insuredPerson?.first_name || insuredPerson?.last_name) {
        insuredPersonName = `${insuredPerson?.first_name || ''} ${insuredPerson?.last_name || ''}`.trim();
      } else if (insuredPerson?.title) {
        insuredPersonName = insuredPerson.title;
      }
      const label = insuredPersonName
        ? `${insurer?.insurer_name || 'Untitled'} (${insuredPersonName})`
        : insurer?.insurer_name || 'Untitled';

      return {
        label: label,
        value: insurer?.insurer_id, // Use insurer_id as value for uniqueness
        insurer_id: insurer?.insurer_id,
        insurer_name: insurer?.insurer_name || 'Untitled',
        insurer_pk: insurer?.pk,
        insurer_sk: insurer?.sk,
        policy_number: insurer?.policy_number || '',
        claim_number: claim_number,
        insurance_limit: insurance_limit,
      };
    });

    return {
      options: updatedOptions || [],
      additional: {
        pageLimit: pageLimit,
        page: page,
      },
      hasMore: options?.data?.has_next,
    };
  };

  return (
    <>
      <div className="caption-bold my-3 d-flex justify-content-between align-items-center">
        <span>Defendants</span>
      </div>
      <Card className="card-bg F-size14 mb-3 info-card">
        {defendants?.length > 0 ? (
          defendants?.map((obj, index) => (
            <>
              <div className="mb-4">
                <span className="F-size16 text-break">{`${obj.first_name} ${obj.last_name}`}</span>
              </div>
              <span className="caption-bold mb-2 mt-3">Defendant Information</span>
              <Card className="card-bg F-size14 mb-4">
                <div className="row align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 text-muted p-medium">Defendant Name</div>
                  <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                    {defendantNameToggle?.[index] ? (
                      <>
                        <Controller
                          name={`defendant.${index}.name`}
                          control={control}
                          render={({ field }) => (
                            <AutoComplete
                              placeholder="Search and Select Defendant"
                              style={{ width: '100%' }}
                              inputStyle={{ width: '100%' }}
                              value={inputValues[obj.contact_id] || getAutoCompleteValue(obj.contact_id)}
                              suggestions={suggestions}
                              onSelect={(e) => onSelectContact(obj.contact_id, e)}
                              completeMethod={searchContact}
                              field="label"
                              onChange={(e) => {
                                field.onChange(e.value);
                                // Update search query for debouncing
                                setSearchQuery(e.value || '');
                                // Update input value for typing
                                setInputValues((prev) => ({
                                  ...prev,
                                  [obj.contact_id]: e.value,
                                }));
                                // Clear selection if user types manually
                                if (e.value && typeof e.value === 'string') {
                                  setSelectedContactId((prev) => ({
                                    ...prev,
                                    [obj.contact_id]: null,
                                  }));
                                  setSelectedContacts((prev) => ({
                                    ...prev,
                                    [obj.contact_id]: null,
                                  }));
                                } else if (!e.value) {
                                  // Clear everything when field is empty
                                  setSelectedContactId((prev) => ({
                                    ...prev,
                                    [obj.contact_id]: null,
                                  }));
                                  setSelectedContacts((prev) => ({
                                    ...prev,
                                    [obj.contact_id]: null,
                                  }));
                                }
                              }}
                              className="flex-grow-1 input-shadow"
                            />
                          )}
                        />

                        <Button
                          icon="pi pi-times"
                          className="p-button-secondary outline mx-2"
                          type="button"
                          onClick={() => {
                            toggleVisibility(index);
                            setSelectedContactId((prev) => ({ ...prev, [obj?.contact_id]: obj.contact_id }));
                          }}
                        />
                        <Button
                          icon="pi pi-check"
                          className="p-button-secondary outline"
                          type="button"
                          onClick={() => handlePopupConfirmation(index, obj?.contact_id)}
                        />
                      </>
                    ) : (
                      <>
                        <Link to={`/contacts/${obj?.contact_id || ''}`} className="text-decoration-none">
                          <div className="title-link-wrap cursor-pointer">
                            <h5 className="title-text mb-0 redirection-link-no-text-transform">{`${obj?.first_name} ${obj?.last_name}`}</h5>
                          </div>
                        </Link>
                        <div className="col-1 col-md-1 px-2 d-flex align-items-center ms-1 w-auto">
                          <span className="pointer">
                            <i
                              className="icon-edit"
                              onClick={() => {
                                setSelectedContactId((prev) => ({ ...prev, [obj.contact_id]: obj.contact_id }));
                                toggleVisibility(index);
                              }}
                            ></i>
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="row align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 text-muted p-medium">Date of Birth</div>
                  <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                    <span>{obj?.dob ? formatDateWithoutTimezone(obj?.dob) : '-'}</span>
                  </div>
                </div>
              </Card>
              <div className="caption-bold my-3 d-flex justify-content-between align-items-center">
                <span className="caption-bold mb-2 mt-3">Defendant Claim Information</span>
                <Button
                  label="Update Claim Info"
                  className="btn-outline p-button-secondary py-1 px-3"
                  type="button"
                  onClick={handleSubmit((data) => handleDefendantClaimInfoSubmit(data, index, obj?.contact_id))}
                />
              </div>
              <Card className="card-bg F-size14 mt-2 mb-5">
                <div className="row align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 text-muted p-medium">Vehicle Info</div>
                  <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                    <Controller
                      name={`defendant.${index}.vehicle_info`}
                      control={control}
                      render={({ field }) => (
                        <TextSnippetWrapper
                          type="text"
                          className="input-shadow w-100"
                          placeholder="Enter Vehicle Info"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="row align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 text-muted p-medium">Vehicle Owner</div>
                  <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                    <Controller
                      name={`defendant.${index}.vehicle_owner`}
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          placeholder="Select Vehicle Owner"
                          value={field.value}
                          options={vehicleOwnerOptions}
                          onChange={(e) => {
                            field.onChange(e.value);
                          }}
                          optionValue=""
                          optionLabel=""
                          className="input-shadow border-0 w-100"
                          filter
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="row align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 text-muted p-medium">Insurer</div>
                  <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                    {/* <Controller
                      name={`defendant.${index}.insurer`}
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          value={field.value}
                          options={insurerOptions}
                          onChange={(e) => {
                            field.onChange(e.value);
                            // Find and store the full insurer object
                            if (e.value) {
                              const selected = insurerOptions.find((opt) => opt.value === e.value);
                              setSelectedInsurers((prev) => ({
                                ...prev,
                                [index]: selected,
                              }));

                              // Auto-populate policy_number, claim_number, and insurance_limits
                              // Only update if insurer has non-empty values, otherwise keep existing values
                              if (selected) {
                                // Get current values from form
                                const currentPolicyNumber = watch(`defendant.${index}.policy_number`);
                                const currentClaimNumber = watch(`defendant.${index}.claim_number`);
                                const currentInsuranceLimits = watch(`defendant.${index}.insurance_limit`);

                                // Only update if insurer value is not empty, otherwise keep existing
                                setValue(`defendant.${index}.policy_number`, selected.policy_number || currentPolicyNumber || '');
                                setValue(`defendant.${index}.claim_number`, selected.claim_number || currentClaimNumber || '');
                                setValue(`defendant.${index}.insurance_limit`, selected.insurance_limit || currentInsuranceLimits || 0);
                              }
                            } else {
                              // Clear selected insurer and auto-populated fields when dropdown is cleared
                              setSelectedInsurers((prev) => {
                                const updated = { ...prev };
                                delete updated[index];
                                return updated;
                              });
                              setValue(`defendant.${index}.policy_number`, '');
                              setValue(`defendant.${index}.claim_number`, '');
                              setValue(`defendant.${index}.insurance_limit`, 0);
                            }
                          }}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Select Insurer"
                          className="input-shadow w-100"
                          filter
                          showClear={!!field.value}
                          filterPlaceholder="Search Insurer"
                        />
                      )}
                    /> */}

                    <Controller
                      name={`defendant.${index}.insurer`}
                      control={control}
                      render={({ field }) => {
                        const selectedOption =
                          typeof field.value === 'string' ? insurerOptions?.find((opt) => opt.value === field.value) || null : field.value;
                        return (
                          <AsyncPaginate
                            value={selectedOption}
                            onChange={(selectedOption) => {
                              field.onChange(selectedOption?.insurer_id);
                              // Find and store the full insurer object
                              if (selectedOption) {
                                const selected = insurerOptions.find((opt) => opt.value === selectedOption);
                                setSelectedInsurers((prev) => ({
                                  ...prev,
                                  [index]: selected,
                                }));

                                // Auto-populate policy_number, claim_number, and insurance_limits
                                // Only update if insurer has non-empty values, otherwise keep existing values
                                if (selected) {
                                  // Get current values from form
                                  const currentPolicyNumber = watch(`defendant.${index}.policy_number`);
                                  const currentClaimNumber = watch(`defendant.${index}.claim_number`);
                                  const currentInsuranceLimits = watch(`defendant.${index}.insurance_limit`);

                                  // Only update if insurer value is not empty, otherwise keep existing
                                  setValue(`defendant.${index}.policy_number`, selected.policy_number || currentPolicyNumber || '');
                                  setValue(`defendant.${index}.claim_number`, selected.claim_number || currentClaimNumber || '');
                                  setValue(`defendant.${index}.insurance_limit`, selected.insurance_limit || currentInsuranceLimits || 0.0);
                                }
                              } else {
                                // Clear selected insurer and auto-populated fields when dropdown is cleared
                                setSelectedInsurers((prev) => {
                                  const updated = { ...prev };
                                  delete updated[index];
                                  return updated;
                                });
                                setValue(`defendant.${index}.policy_number`, '');
                                setValue(`defendant.${index}.claim_number`, '');
                                setValue(`defendant.${index}.insurance_limit`, 0.0);
                              }
                            }}
                            loadOptions={loadInsurerOptions}
                            getOptionLabel={(option) => option?.label || option?.insurer_name}
                            getOptionValue={(option) => option?.value || option?.insurer_id}
                            additional={{
                              pageLimit: 15,
                              page: 0,
                            }}
                            debounceTimeout={300}
                            placeholder="Select Insurer"
                            className="input-shadow w-100"
                            filter
                            isClearable
                            filterPlaceholder="Search Insurer"
                            cacheUniqs={[insurerOptions]}
                            styles={userDetails?.firmDetails?.appearance_preference}
                          />
                        );
                      }}
                    />

                    <div className="col-1 col-md-1 px-1 d-flex align-items-center ms-1 w-auto">
                      <span className="pointer">
                        <i
                          className="fas fa-plus-circle add-ins"
                          onClick={() => {
                            setCurrentDefendantIndex(index);
                            setShowAddInsurerDialog(true);
                          }}
                        ></i>
                        <Tooltip content="Add Insurer" position="top" target=".add-ins" showDelay={500} />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 text-muted p-medium">Policy Number</div>
                  <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                    <Controller
                      name={`defendant.${index}.policy_number`}
                      control={control}
                      render={({ field }) => (
                        <TextSnippetWrapper
                          type="text"
                          className="input-shadow w-100"
                          placeholder="Type In"
                          maxLength={64}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="row align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 text-muted p-medium">Claim number</div>
                  <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                    <Controller
                      name={`defendant.${index}.claim_number`}
                      control={control}
                      render={({ field }) => (
                        <TextSnippetWrapper
                          type="text"
                          className="input-shadow w-100"
                          placeholder="Type In"
                          maxLength={64}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="row align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 text-muted p-medium">Name of Insured</div>
                  <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                    <Controller
                      name={`defendant.${index}.name_of_insured`}
                      control={control}
                      render={({ field }) => (
                        <TextSnippetWrapper
                          type="text"
                          className="input-shadow w-100"
                          placeholder="Type In"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="row align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 text-muted p-medium">Injuries</div>
                  <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                    <Controller
                      name={`defendant.${index}.injuries`}
                      control={control}
                      render={({ field }) => (
                        <TextSnippetWrapper
                          type="text"
                          className="input-shadow w-100"
                          placeholder="Type In"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="row align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 text-muted p-medium">Insurance Limits</div>
                  <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                    <Controller
                      name={`defendant.${index}.insurance_limit`}
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          className="input-shadow w-100"
                          id={field.name}
                          placeholder="Type In"
                          name={field.name}
                          mode="currency"
                          currency="USD"
                          locale="en-US"
                          {...field}
                          onChange={(e) => field.onChange(e.value)}
                        />
                      )}
                    />
                  </div>
                </div>
              </Card>
            </>
          ))
        ) : (
          <span> No Defendant Found</span>
        )}
      </Card>

      <UpdateContactDialog
        visible={showUpdateDialog}
        onHide={handleDialogHide}
        onRetain={handleRetain}
        onReset={handleReset}
        isDefendant={true}
      />

      {/* Add Insurance Dialog */}
      <AddInsurerDialog
        visible={showAddInsurerDialog}
        onHide={() => {
          setShowAddInsurerDialog(false);
          setCurrentDefendantIndex(null);
        }}
        onSubmit={onSubmitInsurer}
        requiredFields={['insurance_company', 'contact_name', 'email', 'phone', 'fax', 'policy_number', 'claim_number', 'insurance_limit']}
        caseDetails={caseDetails}
        caseId={caseDetails?.case_id}
        insuranceProviders={insuranceProviders}
        onInsuranceAdded={loadInsurers}
        availableLawftIds={getAvailableLawftIdsForInsurer()}
      />
    </>
  );
};

export default Defendant;
